import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import Link, { type LinkProps } from "next/link";
import * as React from "react";

import { Loader } from "@/components/ui/loader";
import { cn } from "@/utils/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xs text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-secondary focus-visible:ring-secondary/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer font-mono",
  {
    variants: {
      variant: {
        default: "bg-primary shadow-xs hover:bg-primary/90 text-accent",
        "off-green": "bg-[var(--green-card)] text-accent shadow-xs hover:bg-[var(--green-card)]/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs text-primary hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary: "bg-secondary shadow-xs hover:bg-secondary/80 text-accent",
        ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        transparent: "bg-transparent",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-xs gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-xs px-6 has-[>svg]:px-4",
        xl: "h-12 rounded-xs px-6 has-[>svg]:px-4",
        "2xl": "h-14 rounded-xs px-6 has-[>svg]:px-4 text-base",
        wide: "h-14 rounded-xs has-[>svg]:px-4 px-12",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  disabled,
  loading,
  children,
  leftIcon,
  rightIcon,
  href,
  target,
  rel,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    loading?: boolean;
    href?: LinkProps["href"];
    rel?: React.HTMLAttributes<HTMLAnchorElement>["rel"];
    target?: React.HTMLAttributeAnchorTarget;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <>
      {href && (
        <Link href={href} target={target} rel={rel}>
          <Comp
            className={cn(buttonVariants({ variant, size, className }))}
            disabled={loading ?? disabled}
            {...props}
          >
            {loading && <Loader className={cn("", size !== "icon" && "mr-1")} size={16} />}
            {leftIcon && !loading && <span className="mr-1">{leftIcon}</span>}
            {size === "icon" && !loading && <span>{children}</span>}
            {size !== "icon" && <span>{children}</span>}
            {rightIcon && <span className="ml-1">{rightIcon}</span>}
          </Comp>
        </Link>
      )}

      {!href && (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          disabled={loading ?? disabled}
          {...props}
        >
          {loading && <Loader className={cn("", size !== "icon" && "mr-1")} size={16} />}
          {leftIcon && !loading && <span className="mr-1">{leftIcon}</span>}
          {size === "icon" && !loading && <span>{children}</span>}
          {size !== "icon" && <span>{children}</span>}
          {rightIcon && <span className="ml-1">{rightIcon}</span>}
        </Comp>
      )}
    </>
  );
}

export { Button, buttonVariants };
